/**
 * Layers above the page: the modal, the side drawer and the toasts. Modal and
 * drawer are native <dialog>s opened with showModal(), so the browser keeps
 * focus inside and Escape closes them (routed through `onClose`). Both play an
 * enter and an exit animation; the exit keeps the last content on screen while
 * it fades, and is skipped under reduced motion or when the parent unmounts.
 */
import type { CSSProperties, ReactNode, RefObject } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CircleAlert, CircleCheck, X } from 'lucide-react'

import { IconButton, cx, prefersReducedMotion, toneClass, type Tone } from './primitives'
import styles from './Overlay.module.css'

/* ------------------------------------------------------------------------ */
/* the dialog engine shared by Modal and Drawer                             */
/* ------------------------------------------------------------------------ */

const EXIT_MS = 160

/** Where focus lands on open: the first field, else the selected tab, else the dialog itself. */
function focusFirst(dialog: HTMLDialogElement): void {
  // showModal() lands on the close button; the first field is what the operator came for.
  const field = dialog.querySelector<HTMLElement>('input:not([readonly]):not([type=hidden]), textarea, select, [role=tab][aria-selected=true]')
  ;(field ?? dialog).focus({ preventScroll: true })
}

function useDialog(open: boolean, onClose: () => void): { ref: RefObject<HTMLDialogElement>; closing: boolean } {
  const ref = useRef<HTMLDialogElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const closingByUs = useRef(false)
  const [closing, setClosing] = useState(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open) {
      window.clearTimeout(timer.current)
      setClosing(false)
      if (!dialog.open) {
        dialog.showModal()
        focusFirst(dialog)
      }
      return
    }
    if (!dialog.open) return
    if (prefersReducedMotion()) {
      closingByUs.current = true
      dialog.close()
      return
    }
    setClosing(true)
    timer.current = window.setTimeout(() => {
      closingByUs.current = true
      dialog.close()
      setClosing(false)
    }, EXIT_MS)
  }, [open])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    const cancel = (event: Event): void => {
      event.preventDefault()
      onCloseRef.current()
    }
    // A browser may still close the dialog itself (repeated Escape); keep the parent's state in step.
    const closed = (): void => {
      if (closingByUs.current) {
        closingByUs.current = false
        return
      }
      onCloseRef.current()
    }
    dialog.addEventListener('cancel', cancel)
    dialog.addEventListener('close', closed)
    return () => {
      dialog.removeEventListener('cancel', cancel)
      dialog.removeEventListener('close', closed)
    }
  }, [])

  return { ref, closing }
}

/** Keeps the last content rendered while open, so an exit animation does not show an emptied panel. */
function useHeld<T>(value: T, open: boolean): T {
  const held = useRef(value)
  if (open) held.current = value
  return open ? value : held.current
}

/* ------------------------------------------------------------------------ */
/* modal                                                                    */
/* ------------------------------------------------------------------------ */

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  /** 880 px instead of 560 (same as size="lg"). */
  wide?: boolean
  /** sm 440 · md 560 (default) · lg 880. */
  size?: 'sm' | 'md' | 'lg'
  /** Icon in a tile left of the title. */
  icon?: ReactNode
  /** The icon tile's tone (default accent; `down` for a destructive confirm). */
  tone?: Tone
  /** Buttons, right-aligned; put the primary action last. */
  footer?: ReactNode
  children: ReactNode
}

/** The centred dialog: backdrop blur + fade, panel scales .97→1 in 200 ms; focus trap and Escape kept. */
export function Modal({ open, onClose, title, subtitle, wide = false, size, icon, tone = 'accent', footer, children }: ModalProps): JSX.Element {
  const { ref, closing } = useDialog(open, onClose)
  const body = useHeld(children, open)
  const foot = useHeld(footer, open)
  const sub = useHeld(subtitle, open)
  const width = size ?? (wide ? 'lg' : 'md')
  return (
    <dialog
      ref={ref}
      className={cx(styles.dialog, styles.modal, width === 'lg' ? styles.lg : width === 'sm' ? styles.sm : '')}
      aria-label={title}
      data-closing={closing ? '' : undefined}
    >
      <div className={styles.head}>
        {icon ? <span className={cx(styles.headIcon, toneClass(tone))}>{icon}</span> : null}
        <div className={styles.headText}>
          <h2 className={styles.title}>{title}</h2>
          {sub ? <p className={styles.subtitle}>{sub}</p> : null}
        </div>
        <IconButton icon={<X />} label="Закрыть" size="sm" onClick={onClose} tooltip={false} className={styles.close} />
      </div>
      <div className={styles.body}>{body}</div>
      {foot ? <div className={styles.foot}>{foot}</div> : null}
    </dialog>
  )
}

/* ------------------------------------------------------------------------ */
/* drawer                                                                   */
/* ------------------------------------------------------------------------ */

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  /** Buttons or chips beside the close button. */
  actions?: ReactNode
  footer?: ReactNode
  /** Panel width in px (default 480; never wider than the window). */
  width?: number
  children: ReactNode
}

/** A detail panel that slides in from the right (320 ms); same focus trap and Escape as Modal. */
export function Drawer({ open, onClose, title, subtitle, actions, footer, width = 480, children }: DrawerProps): JSX.Element {
  const { ref, closing } = useDialog(open, onClose)
  const body = useHeld(children, open)
  const foot = useHeld(footer, open)
  const sub = useHeld(subtitle, open)
  const acts = useHeld(actions, open)
  return (
    <dialog
      ref={ref}
      className={cx(styles.dialog, styles.drawer)}
      aria-label={title}
      data-closing={closing ? '' : undefined}
      style={{ width: `min(${width}px, 100vw)` }}
    >
      <div className={styles.head}>
        <div className={styles.headText}>
          <h2 className={styles.title}>{title}</h2>
          {sub ? <p className={styles.subtitle}>{sub}</p> : null}
        </div>
        {acts ? <div className={styles.headActions}>{acts}</div> : null}
        <IconButton icon={<X />} label="Закрыть" size="sm" onClick={onClose} tooltip={false} className={styles.close} />
      </div>
      <div className={cx(styles.body, styles.drawerBody)}>{body}</div>
      {foot ? <div className={styles.foot}>{foot}</div> : null}
    </dialog>
  )
}

/* ------------------------------------------------------------------------ */
/* toasts                                                                   */
/* ------------------------------------------------------------------------ */

interface ToastItem {
  id: number
  text: string
  error: boolean
  leaving: boolean
}

const ToastContext = createContext<(text: string, error?: boolean) => void>(() => undefined)
const LEAVE_MS = 180

/**
 * Hosts the toasts: bottom-right, slide in, a bar that counts down the
 * auto-dismiss (3.2 s, errors 6 s), paused while the pointer is over it.
 * `useToast()` returns `(text, error?) => void`.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const show = useCallback((text: string, error = false) => {
    const id = nextId.current++
    setItems((current) => [...current.filter((item) => !item.leaving).slice(-3), { id, text, error, leaving: false }])
  }, [])
  const dismiss = useCallback((id: number) => {
    if (prefersReducedMotion()) {
      setItems((current) => current.filter((item) => item.id !== id))
      return
    }
    setItems((current) => current.map((item) => (item.id === id ? { ...item, leaving: true } : item)))
    window.setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), LEAVE_MS)
  }, [])
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={styles.toasts} role="status" aria-live="polite">
        {items.map((item) => (
          <Toast key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }): JSX.Element {
  const ttl = item.error ? 6000 : 3200
  const [paused, setPaused] = useState(false)
  const remaining = useRef(ttl)
  const startedAt = useRef(0)
  useEffect(() => {
    if (paused || item.leaving) return
    startedAt.current = performance.now()
    const timer = window.setTimeout(() => onDismiss(item.id), remaining.current)
    return () => {
      window.clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt.current))
    }
  }, [paused, item.leaving, item.id, onDismiss])
  return (
    <div
      className={cx(styles.toast, item.error ? styles.toastError : '')}
      data-leaving={item.leaving ? '' : undefined}
      data-paused={paused ? '' : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ '--ttl': `${ttl}ms` } as CSSProperties}
    >
      <span className={styles.toastIcon} aria-hidden="true">
        {item.error ? <CircleAlert /> : <CircleCheck />}
      </span>
      <span className={styles.toastText}>{item.text}</span>
      <IconButton icon={<X />} label="Скрыть" size="sm" tooltip={false} className={styles.toastClose} onClick={() => onDismiss(item.id)} />
      <span className={styles.toastBar} aria-hidden="true" />
    </div>
  )
}

/** Shows a toast: `toast('Сохранено')`, `toast(describeError(error), true)`. */
export function useToast(): (text: string, error?: boolean) => void {
  return useContext(ToastContext)
}
